import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "outer" }
const _hoisted_2 = { key: 0 }

import { ref, useTemplateRef, defineAsyncComponent } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { showError } from '@services/utils/dialog';

import NcButton from '@nextcloud/vue/components/NcButton';
import Modal from './Modal.vue';
import FaceList from './FaceList.vue';

import { useModal } from '@services/modal';
import { t } from '@services/l10n';
import { useRouteIs } from '@services/route-is';
import { isAbortError, useAbort } from '@services/utils/abort-vue';
import client from '@services/dav/client';
import * as dav from '@services/dav';
import * as utils from '@services/utils/common';

import type { IFileInfo, IFace } from '@typings';


export default /*@__PURE__*/_defineComponent({
  ...{
  name: 'FaceMergeModal',
},
  __name: 'FaceMergeModal',
  setup(__props, { expose: __expose }) {

const NcProgressBar = defineAsyncComponent(() => import('@nextcloud/vue/components/NcProgressBar'));



const route = useRoute();
const router = useRouter();
const routeIs = useRouteIs();
const modal = useTemplateRef('modal');
const { show, close } = useModal(modal);
const processing = ref(0);
const processingTotal = ref(0);
const abort = useAbort();

function open() {
  const user = route.params.user?.toString() || '';
  if (route.params.user?.toString() !== utils.uid) {
    showError(
      t('memories', 'Only user "{user}" can update this person', {
        user,
      }),
    );
    return;
  }
  show.value = true;
}

function cleanup() {
  abort.abort();
  show.value = false;
  processing.value = 0;
  processingTotal.value = 0;
}

async function clickFace(face: IFace) {
  const user = route.params.user?.toString() || '';
  const name = route.params.name?.toString() || '';

  const newName = String(face.name || face.cluster_id);

  if (
    !(await utils.confirmDestructive({
      title: t('memories', 'Merge faces'),
      message: t('memories', 'Merge {name} with {newName}?', {
        name: utils.isNumber(name) ? t('memories', 'Unnamed person') : name,
        newName: utils.isNumber(newName) ? t('memories', 'Unnamed person') : newName,
      }),
      confirm: t('memories', 'Continue'),
      confirmClasses: 'error',
      cancel: t('memories', 'Cancel'),
    }))
  ) {
    return;
  }

  if (routeIs.FaceRecognition) {
    if (Number.isInteger(Number(newName))) {
      showError(t('memories', 'You can only merge with a named person'));
      return;
    }
    try {
      const signal = abort.renew();
      await dav.faceRecognitionRenamePerson(name, newName, { signal });
      signal.throwIfAborted();
      await close();
      await router.replace({
        name: 'facerecognition',
        params: { user: face.user_id, name: newName },
      });
    } catch (error) {
      if (isAbortError(error)) return;
      console.error(error);
      showError(t('memories', 'Failed to move {name}.', { name }));
    }
    return;
  }

  try {
    // Get all files for current face
    const signal = abort.renew();
    let res = (await client.getDirectoryContents(`/recognize/${user}/faces/${name}`, {
      details: true,
      signal,
    })) as any;
    signal.throwIfAborted();
    let data: IFileInfo[] = res.data;
    processingTotal.value = data.length;

    // Don't try too much
    let failures = 0;

    // Create move calls
    const calls = data.map((p) => async () => {
      // Short circuit if we have too many failures
      if (failures === 10) {
        showError(t('memories', 'Too many failures, aborting'));
        failures++;
      }
      if (failures >= 10) return;
      signal.throwIfAborted();

      // Move to new face with webdav
      try {
        await client.moveFile(
          `/recognize/${user}/faces/${name}/${p.basename}`,
          `/recognize/${face.user_id}/faces/${newName}/${p.basename}`,
          { signal },
        );
      } catch (e) {
        if (isAbortError(e)) throw e;
        console.error(e);
        showError(t('memories', 'Error while moving {basename}', p));
        failures++;
      } finally {
        if (!signal.aborted) processing.value++;
      }
    });
    for await (const _ of dav.runInParallel(calls, 10, { signal })) {
      // nothing to do
    }
    signal.throwIfAborted();

    // Go to new face
    if (failures === 0) {
      await close();
      await router.replace({
        name: 'recognize',
        params: { user: face.user_id, name: newName },
      });
    }
  } catch (error) {
    if (isAbortError(error)) return;
    console.error(error);
    showError(t('memories', 'Failed to move {name}.', { name }));
  }
}

__expose({ open });

return (_ctx: any,_cache: any) => {
  return (_unref(show))
    ? (_openBlock(), _createBlock(Modal, {
        key: 0,
        ref_key: "modal",
        ref: modal,
        onClose: cleanup,
        size: "large"
      }, {
        title: _withCtx(() => [
          _createTextVNode(_toDisplayString(_unref(t)('memories', 'Merge {name} with person', { name: _ctx.$route.params.name })), 1)
        ]),
        buttons: _withCtx(() => [
          _createVNode(_unref(NcButton), {
            onClick: _unref(close),
            class: "button",
            variant: "error"
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_unref(t)('memories', 'Cancel')), 1)
            ]),
            _: 1
          }, 8, ["onClick"])
        ]),
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_1, [
            _createVNode(FaceList, { onSelect: clickFace }),
            (processingTotal.value > 0)
              ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                  _createVNode(_unref(NcProgressBar), {
                    value: Math.round((processing.value * 100) / processingTotal.value),
                    error: true
                  }, null, 8, ["value"])
                ]))
              : _createCommentVNode("", true)
          ])
        ]),
        _: 1
      }, 512))
    : _createCommentVNode("", true)
}
}

})